import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import '../css/UXModal.css';

const UXModalContext = createContext(null);

export function useUXModal() {
  const context = useContext(UXModalContext);
  if (!context) {
    throw new Error('useUXModal deve essere usato dentro UXModalProvider');
  }
  return context;
}

export default function UXModalProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const resolverRef = useRef(null);

  const closeDialog = (result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    setPromptValue('');
    if (resolve) {
      resolve(result);
    }
  };

  const openDialog = (nextDialog) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setPromptValue(nextDialog.defaultValue || '');
    setDialog(nextDialog);
  });

  const api = useMemo(() => ({
    alert: ({ title = 'Avviso', message = '', confirmText = 'OK', confirmIntent = 'primary' }) => (
      openDialog({ type: 'alert', title, message, confirmText, confirmIntent })
    ),
    confirm: ({
      title = 'Conferma azione',
      message = '',
      confirmText = 'Conferma',
      cancelText = 'Annulla',
      confirmIntent = 'primary',
      cancelOnRight = false
    }) => (
      openDialog({ type: 'confirm', title, message, confirmText, cancelText, confirmIntent, cancelOnRight })
    ),
    prompt: ({
      title = 'Inserisci un valore',
      message = '',
      confirmText = 'Continua',
      cancelText = 'Annulla',
      defaultValue = '',
      placeholder = '',
      confirmIntent = 'primary'
    }) => (
      openDialog({ type: 'prompt', title, message, confirmText, cancelText, defaultValue, placeholder, confirmIntent })
    )
  }), []);

  return (
    <UXModalContext.Provider value={api}>
      {children}

      {dialog && (
        <div className="ux-modal-backdrop" role="presentation" onMouseDown={() => closeDialog(dialog.type === 'confirm' ? false : null)}>
          <div
            className="ux-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ux-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h3 id="ux-modal-title" className="ux-modal-title">{dialog.title}</h3>
            {dialog.message ? <p className="ux-modal-message">{dialog.message}</p> : null}

            {dialog.type === 'prompt' && (
              <input
                className="ux-modal-input"
                type="text"
                value={promptValue}
                placeholder={dialog.placeholder || ''}
                autoFocus
                onChange={(event) => setPromptValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    closeDialog(promptValue);
                  }
                  if (event.key === 'Escape') {
                    closeDialog(null);
                  }
                }}
              />
            )}

            <div className="ux-modal-actions">
              {dialog.type !== 'alert' && !dialog.cancelOnRight && (
                <button
                  type="button"
                  className="ux-modal-btn ux-modal-btn-cancel"
                  onClick={() => closeDialog(dialog.type === 'confirm' ? false : null)}
                >
                  {dialog.cancelText || 'Annulla'}
                </button>
              )}
              <button
                type="button"
                className={`ux-modal-btn ux-modal-btn-confirm ${dialog.confirmIntent === 'danger' ? 'ux-modal-btn-danger' : ''}`}
                onClick={() => {
                  if (dialog.type === 'alert') {
                    closeDialog(true);
                    return;
                  }
                  if (dialog.type === 'confirm') {
                    closeDialog(true);
                    return;
                  }
                  closeDialog(promptValue);
                }}
              >
                {dialog.confirmText || 'OK'}
              </button>
              {dialog.type !== 'alert' && dialog.cancelOnRight && (
                <button
                  type="button"
                  className="ux-modal-btn ux-modal-btn-cancel"
                  onClick={() => closeDialog(dialog.type === 'confirm' ? false : null)}
                >
                  {dialog.cancelText || 'Annulla'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </UXModalContext.Provider>
  );
}
